import { useContext, useEffect, useState } from "react";
import { DropdownOption, mapToDropdownOption, getById, ensure, ValidationResult } from "sprint-common-web";

import { useAirlines } from "../../../../../common/data/data-hooks/useArilines";
import { useNonTrailerVersionTypes } from "../../../../../common/data/data-hooks/useNonTrailerVersionTypes";
import { useTrailerVersionTypes } from "../../../../../common/data/data-hooks/useTrailerVersionTypes";
import { DistributorsDataContext } from "../../../../../common/data/lookup-data-providers/DistributorsDataProvider";
import { UpdateMovieVersionDetailsRequest } from "../../../api/UpdateMovieVersionDetailsRequest";
import { useAssetsManageMovieApi } from "../../../api/useAssetsManageMovieApi";
import { ValidateUpdateMovieVersionDetailsRequest } from "../../../api/ValidateUpdateMovieVersionDetailsRequest";
import { MovieContext } from "../../context/MovieContext";
import { MovieDetails, MovieVersionDetails } from "../../MovieDetails";
import { UpdatedVersionData } from "./EditMovieVersionForm";
import { EditMovieVersionFormInputs } from "./EditMovieVersionFormInputs";

export const useEditMovieVersionForm = (movieVersionId: string) => {
    const { movie } = useContext(MovieContext);
    ensure(movie);

    const { data: airlines } = useAirlines();
    const { getDistributorById } = useContext(DistributorsDataContext);
    const { data: trailerVersionTypes } = useTrailerVersionTypes();
    const { data: nonTrailerVersionTypes } = useNonTrailerVersionTypes();
    const { validateUpdateMovieVersionDetails, updateMovieVersionDetails } = useAssetsManageMovieApi();

    const selectedMovieVersion = getById(movie.versions, movieVersionId);

    const [isTrailerSelected, setIsTrailerSelected] = useState(
        !!trailerVersionTypes?.find((v) => v.id === selectedMovieVersion.type.id)
    );

    useEffect(() => {
        setIsTrailerSelected(!!trailerVersionTypes?.find((v) => v.id === selectedMovieVersion.type.id));
    }, [trailerVersionTypes]);

    const versionTypes = isTrailerSelected ? trailerVersionTypes : nonTrailerVersionTypes;

    const { defaultValues, defaultOptions } = getDefaultValues(movie, movieVersionId);

    const validateEditMovieVersion = async (inputs: EditMovieVersionFormInputs): Promise<ValidationResult> => {
        return validateUpdateMovieVersionDetails(movie.id, movieVersionId, createValidationRequest(inputs));
    };

    const editMovieVersion = async (inputs: EditMovieVersionFormInputs): Promise<UpdatedVersionData> => {
        return updateMovieVersionDetails(movie.id, movieVersionId, createUpdateRequest(inputs)).then(() =>
            getUpdatedVersionData(inputs)
        );
    };

    const getUpdatedVersionData = async (inputs: EditMovieVersionFormInputs): Promise<UpdatedVersionData> => {
        let distributor = selectedMovieVersion.distributor;
        if (selectedMovieVersion.distributor.id !== inputs.distributorId) {
            distributor = await getDistributorById(inputs.distributorId);
        }
        const versionType = getById(versionTypes ?? [], inputs.versionTypeId);

        return {
            versionId: movieVersionId,
            distributor,
            type: versionType,
            isTrailer: isTrailerSelected
        };
    };

    return {
        defaultValues,
        defaultOptions,
        isTrailerSelected,
        setIsTrailerSelected,
        airlines: airlines ?? [],
        versionTypes,
        trailerVersionTypes,
        selectedMovieVersion,
        validateEditMovieVersion,
        editMovieVersion
    };
};

const createValidationRequest = (inputs: EditMovieVersionFormInputs): ValidateUpdateMovieVersionDetailsRequest => {
    return {
        typeId: inputs.versionTypeId,
        distributorId: inputs.distributorId,
        brandingIds: inputs.brandingIds
    };
};

const createUpdateRequest = (inputs: EditMovieVersionFormInputs): UpdateMovieVersionDetailsRequest => {
    return {
        distributorId: inputs.distributorId,
        typeId: inputs.versionTypeId
    };
};

interface EditMovieVersionFormDefaults {
    defaultValues: EditMovieVersionFormInputs;
    defaultOptions: {
        distributors: DropdownOption[];
    };
}

const getDefaultValues = (movie: MovieDetails, versionId: string): EditMovieVersionFormDefaults => {
    const defaultVersion = movie.versions.find((version: MovieVersionDetails) => version.id === versionId);

    if (defaultVersion) {
        return {
            defaultValues: {
                distributorId: defaultVersion.distributor.id,
                versionTypeId: defaultVersion.type.id,
                brandingIds: defaultVersion.brandings?.map((b) => b.id) ?? []
            },
            defaultOptions: {
                distributors: [mapToDropdownOption(defaultVersion.distributor)]
            }
        };
    }

    return {
        defaultValues: { distributorId: "", versionTypeId: "", brandingIds: [] },
        defaultOptions: { distributors: [] }
    };
};
